import type {
    AriaDatapointType,
    AnalyticsOptions,
    FlightControl,
    InternalDatapointOptions,
} from 'owa-analytics-types';
import type { AccountKey } from 'owa-client-types';
import { getAnalyticsFlightsAndAppSettings } from '../settings/getAnalyticsFlightsAndAppSettings';

export default function getFlightControl(
    datapoint: AriaDatapointType,
    analyticsOptions: AnalyticsOptions,
    eventType: string,
    accountKey: AccountKey | 'globalAccount' = 'globalAccount'
): FlightControl | null {
    const datapointOptions = datapoint.getOptions() as InternalDatapointOptions;
    const flightsAndAppSettings = getAnalyticsFlightsAndAppSettings(accountKey);

    // Check if isCore is disabled in ECS for this datapoint
    if (
        datapointOptions?.isCore &&
        flightsAndAppSettings?.disableIsCoreDatapoints?.includes(datapoint.getEventName())
    ) {
        datapointOptions.isCore = false;
    }

    // do not sample datapoints if marked as isCore or in ECS unsample override list, and they are not in ECS sampled override list
    if (datapointOptions?.isCore) {
        return null;
    }

    // Ring-level opt-out (e.g. SDF / Dogfood / PPE via ring-dogfood / an-no-sample-ppe):
    // the flight/ECS settings declare this ring fully unsampled, so no flight
    // control applies for ANY app and the event is captured at 100%. This check
    // must run before the flight-control resolution below so the global ECS
    // flight controls never re-introduce sampling on these rings.
    if (flightsAndAppSettings?.skipFlightControls) {
        return null;
    }

    // Throttled high-volume events are rerouted onto the client_verbose flight
    // control so they pick up its (more aggressive) sampling. Gate on the presence
    // of the rerouted-to control (client_verbose) so the decision is driven by the
    // effective target rather than the original event type, and so the
    // empty-by-default global map never spuriously reroutes. logAsVerbose always
    // reroutes. (This only changes which flight control governs sampling; the event
    // table is decided separately by getEventType via isVerbose.)
    const hasVerboseControl =
        !!analyticsOptions?.flightControls?.['client_verbose'] ||
        !!flightsAndAppSettings?.flightControls?.['client_verbose'];
    if (
        (hasVerboseControl &&
            flightsAndAppSettings?.throttledDatapoints?.includes(datapoint.getEventName())) ||
        datapointOptions.logAsVerbose
    ) {
        eventType = 'client_verbose';
    }

    // Flight/ECS sampling is the source of truth across ALL apps: prefer an
    // app-supplied flight control (AnalyticsOptions.flightControls, injected per
    // bootstrap), then fall back to the global ECS-configured flight control so
    // apps that don't wire up their own (e.g. Bookings) are sampled consistently.
    // Apps that never initialize analytics (e.g. Storybook) have undefined
    // analyticsOptions.flightControls and rely solely on the global config (or
    // null when neither is configured).
    return (
        analyticsOptions?.flightControls?.[eventType] ??
        flightsAndAppSettings?.flightControls?.[eventType] ??
        null
    );
}
